export const CONTACT_TYPES = ['email', 'mobile'] as const;
export type ContactType = (typeof CONTACT_TYPES)[number];

export interface StorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

class BrowserSessionStorageAdapter implements StorageAdapter {
  getItem(key: string): string | null {
    return sessionStorage.getItem(key);
  }
  setItem(key: string, value: string): void {
    sessionStorage.setItem(key, value);
  }
  removeItem(key: string): void {
    sessionStorage.removeItem(key);
  }
}

export class AuthSessionStorage {
  private static readonly KEYS = {
    IDENTIFIER: 'principal',
    ACCESS_TOKEN: 'edge.access.token',
    CONTACT_TYPE: 'edge.contact.type',
  } as const;

  private static adapter: StorageAdapter = new BrowserSessionStorageAdapter();

  static configure(adapter: StorageAdapter): void {
    AuthSessionStorage.adapter = adapter;
  }
  static setToken(token: string): void {
    AuthSessionStorage.adapter.setItem(AuthSessionStorage.KEYS.ACCESS_TOKEN, token);
  }

  static getToken(): string | null {
    return AuthSessionStorage.adapter.getItem(AuthSessionStorage.KEYS.ACCESS_TOKEN);
  }

  static getTokenOrThrow(): string {
    const token = AuthSessionStorage.getToken();
    if (!token) throw new Error('Auth token is not present in session storage.');
    return token;
  }

  static removeToken(): void {
    AuthSessionStorage.adapter.removeItem(AuthSessionStorage.KEYS.ACCESS_TOKEN);
  }

  static setIdentifier(identifier: string): void {
    AuthSessionStorage.adapter.setItem(AuthSessionStorage.KEYS.IDENTIFIER, identifier);
  }

  static getIdentifier(): string | null {
    return AuthSessionStorage.adapter.getItem(AuthSessionStorage.KEYS.IDENTIFIER);
  }

  static removeIdentifier(): void {
    AuthSessionStorage.adapter.removeItem(AuthSessionStorage.KEYS.IDENTIFIER);
  }

  static setContactType(type: ContactType): void {
    AuthSessionStorage.adapter.setItem(AuthSessionStorage.KEYS.CONTACT_TYPE, type);
  }

  static getContactType(): ContactType {
    const type = AuthSessionStorage.adapter.getItem(AuthSessionStorage.KEYS.CONTACT_TYPE);
    return CONTACT_TYPES.includes(type as ContactType) ? (type as ContactType) : 'mobile';
  }

  static removeContactType(): void {
    AuthSessionStorage.adapter.removeItem(AuthSessionStorage.KEYS.CONTACT_TYPE);
  }

  static clear(): void {
    AuthSessionStorage.removeToken();
    AuthSessionStorage.removeIdentifier();
    AuthSessionStorage.removeContactType();
  }
}
